<script setup>
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import DataTable from '@/components/DataTable.vue'
  import TextDialog from '@/components/TextDialog.vue'

  import { ref, onMounted, computed, nextTick } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useUserStore } from '@/stores/userStore'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useContractStore } from '@/stores/contractStore'
  import { convertToFullWidth } from '@/components/logics/Util'

  const router = useRouter()
  const route = useRoute()
  const query = route.query

  const userStore = useUserStore()
  const dialogStore = useDialogStore()
  const contractStore = useContractStore()

  // パンくずリスト
  const breadcrumbs = [{ name: 'ダッシュボード', url: 'dashboard' }, { name: 'ユーザーリスト' }]

  // リファレンス変数
  const showPopupEdit = ref(false)
  const isEdit = ref(false)

  const searchText = ref('')
  const sortKey = ref('')
  const sortDesc = ref(false)
  const length = ref('')

  const showPopupPassword = ref(false)
  const showPasswordCurrent = ref(false)
  const currentPassword = ref('')
  const showPasswordChange = ref(false)
  const newPassword = ref('')
  const showPassword = ref(false)

  const showPopupPasskey = ref(false)
  const passkeyName = ref('Windows')

  const showPopupEmailChange = ref(false)
  const newEmail = ref('')
  const targetUserId = ref(null)

  // アイコン
  const selectedFile = ref(null)
  const previewUrl = ref('')

  // computed ////////////////////////////////////////

  // ユーザー権限チェック
  const isUser = computed(() => {
    // currentUserが取得できていない場合は安全のためtrueを返す
    if (!userStore.currentUser) {
      return true
    }
    return !userStore.isAdmin()
  })

  // 編集権限チェック
  const canEdit = computed(() => {
    if (userStore.isRoot()) {
      return true
    }

    // 表示中のユーザーが自分の場合
    if (userStore.isShowingCurrentUser()) {
      return true
    }

    // 自分がマネージャーの場合は、ルートユーザー以外は編集できる
    if (userStore.isManager()) {
      // ユーザーリストから現在のユーザーIDのユーザーを取得
      const role = userStore.getCurrentUserRole()
      if (role) {
        return role !== 'root'
      }
    }

    return false
  })

  // ユーザータイプの編集可否
  const userRoleDisabled = computed(() => {
    // 新規作成の場合は常に変更可能
    if (!isEdit.value) {
      return false
    }

    // 編集時：rootユーザーの場合は権限変更不可
    if (userStore.formData && userStore.formData.role === 'root') {
      return true
    }

    // 自分のユーザータイプは変更できない
    if (userStore.isAdmin() && !userStore.isShowingCurrentUser()) {
      return false
    }

    return true
  })

  // パスワード保存ボタンの有効無効
  const isDisabledPasswordSave = computed(() => {
    return isEdit.value ? !currentPassword.value || !newPassword.value : !newPassword.value
  })

  const showDeleteButton = computed(() => isEdit.value && canEdit.value && !userStore.isShowingCurrentUser())
  const showCopyButton = computed(() => isEdit.value)
  const showSaveButton = computed(() => !isEdit.value || (isEdit.value && canEdit.value))
  const saveButtonText = computed(() => (isEdit.value ? '保存' : '登録'))

  // 入力ハンドラ /////////////////////////////////////////
  // 全角文字入力の処理
  const handleFullWidthInput = (event, key) => {
    userStore.formData[key] = convertToFullWidth(event.target.value)
  }

  // メールアドレス入力時：使えない文字を即座に除去
  const handleEmailInput = event => {
    // メールで使える文字以外を除去（a-zA-Z0-9._%+-@のみ許可）
    const filtered = event.target.value.replace(/[^a-zA-Z0-9._%+\-@]/g, '')

    // フィルタリング後の値をv-modelに反映
    userStore.formData.email = filtered

    // input欄にも即時反映
    event.target.value = filtered
  }

  // メール変更ダイアログ用のメールアドレス入力ハンドラー
  const handleNewEmailInput = event => {
    // メールで使える文字以外を除去（a-zA-Z0-9._%+-@のみ許可）
    const filtered = event.target.value.replace(/[^a-zA-Z0-9._%+\-@]/g, '')

    // フィルタリング後の値をnewEmailに反映
    newEmail.value = filtered

    // input欄にも即時反映
    event.target.value = filtered
  }
  // methods ////////////////////////////////////////

  onMounted(async () => {
    // 現在のユーザー情報を先に取得
    await userStore.getCurrentUser()

    // データ取得
    await userStore.getUsers()
    userStore.initTableList()

    // クエリパラメータに応じて処理
    if (query.action === 'new') {
      openEditUser()
    }
    if (query.id) {
      const user = userStore.getUserById(query.id)
      if (user) {
        openEditUser(user)
      }
    }
  })

  // パスワードダイアログ表示
  const showPasswordDialog = () => {
    currentPassword.value = ''
    newPassword.value = ''
    showPopupPassword.value = true
  }

  // パスワード変更
  const changePassword = async () => {
    if (isEdit.value) {
      const result = await userStore.changePassword(currentPassword.value, newPassword.value)
      if (result) {
        await dialogStore.showMessageDialog('成功', 'パスワードを更新しました')
        showPopupPassword.value = false
        newPassword.value = ''
        currentPassword.value = ''
      }
    }
  }

  // パスキー編集
  const openPasskeyDialog = () => {
    currentPassword.value = ''
    passkeyName.value = 'Windows'
    showPopupPasskey.value = true
  }

  // パスキー登録
  const registerPasskey = async () => {
    const message = await userStore.registerPasskey(currentPassword.value, passkeyName.value)

    if (message) {
      await dialogStore.showMessageDialog('成功', message)
      showPopupPasskey.value = false
    } else {
      await dialogStore.showAlertDialog('エラー', 'パスキー登録に失敗しました')
    }
  }

  // パスキー削除
  const deletePasskey = async passkeyId => {
    if (await dialogStore.showConfirmDialog('確認', 'このパスキーを削除してもよろしいですか？')) {
      const result = await userStore.deletePasskey(passkeyId)

      if (result) {
        await dialogStore.showMessageDialog('成功', 'パスキーを削除しました')
      } else {
        await dialogStore.showAlertDialog('エラー', 'パスキー削除に失敗しました')
      }
    }
  }

  // ファイル選択時の処理
  const onFileSelected = async event => {
    // 契約有効チェック
    if (!(await contractStore.getIsActive())) {
      await dialogStore.showAlertDialog('エラー', '有料プランの契約が必要です')
      return
    }

    const file = event.target.files[0]
    if (!file) return

    // ファイルサイズチェック (2MB以下)
    if (file.size > 2 * 1024 * 1024) {
      await dialogStore.showAlertDialog('エラー', 'ファイルサイズは2MB以下にしてください')
      return
    }

    // 画像ファイルかチェック
    if (!file.type.match('image.*')) {
      await dialogStore.showAlertDialog('エラー', '画像ファイルを選択してください')
      return
    }

    selectedFile.value = file

    // プレビュー生成
    const reader = new FileReader()
    reader.onload = async e => {
      previewUrl.value = e.target.result
      // ファイル選択後、自動でアップロード処理を実行
      await uploadIcon()
    }
    reader.readAsDataURL(file)
  }

  // アイコンのアップロード処理
  const uploadIcon = async () => {
    if (!selectedFile.value) return

    const message = await userStore.uploadUserIcon(userStore.formData.id, selectedFile.value)

    if (message) {
      // アップロード成功時の処理
      previewUrl.value = ''
      selectedFile.value = null

      await dialogStore.showMessageDialog('成功', message)
    } else {
      // アップロード失敗時の処理
      await dialogStore.showAlertDialog('エラー', 'アイコンのアップロードに失敗しました')
      // プレビューをクリア
      previewUrl.value = ''
      selectedFile.value = null
    }
  }

  // アイコンの削除処理
  const removeIcon = async () => {
    const message = await userStore.deleteUserIcon(userStore.formData.id)

    if (message) {
      await dialogStore.showMessageDialog('成功', message)
    }
  }

  // 保存
  const onSave = async () => {
    const message = await userStore.saveUser()
    if (message) {
      await dialogStore.showMessageDialog('成功', message)
      showPopupEdit.value = false
    }
  }

  // コピー
  const onCopy = () => {
    userStore.copyUser()
    newPassword.value = ''
    currentPassword.value = ''
    isEdit.value = false
  }

  // 削除
  const onDelete = async () => {
    if (await dialogStore.showConfirmDialog('確認', 'このユーザーを削除してもよろしいですか？')) {
      const result = await userStore.deleteUser()
      if (result) {
        await dialogStore.showMessageDialog('成功', 'ユーザーを削除しました')
        showPopupEdit.value = false
      }
    }
  }

  // ユーザー編集ダイアログを開く
  const openEditUser = async item => {
    // 現在のユーザー情報が取得できていない場合は先に取得
    if (!userStore.currentUser) {
      await userStore.getCurrentUser()
    }

    // 一般ユーザーの場合、自分以外のユーザーの詳細は開けない
    if (isUser.value && item && item.id !== userStore.currentUser?.id) {
      await dialogStore.showAlertDialog('エラー', '他のユーザーの詳細を表示する権限がありません')
      return
    }

    isEdit.value = !!item
    showPassword.value = false
    await userStore.setFormData(item?.id)
    showPopupEdit.value = true
  }

  // OTP有効化/無効化 TODO
  const onOTPClick = async () => {
    if (!userStore.formData.isOtpEnabled) {
      // 契約有効チェック
      if (!(await contractStore.getIsActive())) {
        await dialogStore.showAlertDialog('エラー', '有料プランの契約が必要です')
        return
      }
      // OTP有効化 ページ遷移
      router.push({ path: `/onetime` })
    } else {
      // OTP無効化
      const message = await userStore.disableOTP()
      if (message) {
        await dialogStore.showMessageDialog('成功', message)
        // ユーザーリストを再取得して表示を更新
        await userStore.getUsers()
      }
    }
  }

  // アカウント
  const account = () => {
    if (userStore.currentUser) {
      openEditUser(userStore.currentUser)
    }
  }

  // 行のアクション処理
  const handleRowAction = ({ table, item }) => {
    if (table.key === 'lastLoginAt') {
      // 認証メール再送信
      sendVerificationEmail(item.id)
    } else if (table.key === 'changeEmail') {
      // メールアドレス変更ダイアログを表示
      openEmailChangeDialog(item.id)
    }
  }

  // 認証メール送信処理
  const sendVerificationEmail = async userId => {
    // 確認ダイアログを表示
    if (!(await dialogStore.showConfirmDialog('確認', '認証メールを再送信してもよろしいですか？'))) {
      return
    }
    const message = await userStore.sendVerificationEmail(userId)
    if (message) {
      await dialogStore.showMessageDialog('確認', message)
      // ユーザーリストを再取得して表示を更新
      await userStore.getUsers()
    }
  }

  // メールアドレス変更ダイアログを開く
  const openEmailChangeDialog = userId => {
    targetUserId.value = userId
    newEmail.value = ''
    showPopupEmailChange.value = true
  }

  // メールアドレス変更処理
  const changeUserEmail = async () => {
    if (!newEmail.value) {
      await dialogStore.showAlertDialog('エラー', 'メールアドレスを入力してください')
      return
    }

    // 確認ダイアログを表示
    if (!(await dialogStore.showConfirmDialog('確認', 'メールアドレスを変更してもよろしいですか？'))) {
      return
    }

    const message = await userStore.changeUserEmail(targetUserId.value, newEmail.value)
    if (message) {
      await dialogStore.showMessageDialog('確認', message)
      showPopupEmailChange.value = false
      newEmail.value = ''
      targetUserId.value = null
    }
  }

  // 日付フォーマット
  const formatDate = value => {
    if (!value) return '-'
    // ISO形式の日付を yyyy/MM/dd に変換
    return value.split('T')[0].replace(/-/g, '/')
  }
</script>

<template>
  <Header @account="account" />
  <main class="app-main">
    <div class="main-header">
      <h2>ユーザーリスト</h2>
      <button type="button" class="new" @click="openEditUser()" v-if="!isUser">新規登録</button>
      <div class="filters">
        <div class="search-box">
          <span class="material-symbols-outlined">search</span>
          <input type="search" name="search-filter" id="search-filter" v-model="searchText" placeholder="検索" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" data-lpignore="true" data-form-type="search" data-1p-ignore="true" data-bwignore="true" readonly onfocus="this.removeAttribute('readonly');" />
        </div>
        <div class="search-result">
          {{ length }}
        </div>
      </div>
    </div>
    <div class="main-body">
      <DataTable :table-list="userStore.tableList" :data-list="userStore.users || []" :search-text="searchText" :sort-key="sortKey" :sort-desc="sortDesc" @select="openEditUser" @length="length = $event" @row-action="handleRowAction" />
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />

  <!-- ユーザー編集ダイアログ -->
  <EditDialog v-model:visible="showPopupEdit" :show-delete-button="showDeleteButton" :show-copy-button="showCopyButton" :show-save-button="showSaveButton" :save-button-text="saveButtonText" @save="onSave" @copy="onCopy" @delete="onDelete">
    <template v-slot:title>{{ isEdit ? '編集' : '新規登録' }}</template>
    <template v-slot:content>
      <div class="dialog-edit">
        <!-- 隠しフィールドを追加して、ブラウザのオートコンプリートを混乱させる -->
        <input type="text" style="display: none" name="preventautofill" id="preventautofill" value=" " />
        <input type="password" style="display: none" name="preventautofillpassword" id="preventautofillpassword" value=" " />

        <form autocomplete="off" id="user-form-random">
          <dl>
            <dt v-if="isEdit">アイコン画像</dt>
            <dd v-if="isEdit">
              <div class="profile-image-container">
                <!-- アイコンプレビュー表示 -->
                <div class="profile-image-preview" v-if="userStore.formData.iconPath || previewUrl">
                  <img :src="previewUrl + userStore.formData.iconPath" alt="プロフィール画像" style="" />
                </div>
                <!-- 操作ボタン -->
                <div class="profile-image-controls">
                  <!-- アイコン選択がない場合、または選択中でアップロード中でない場合 -->
                  <template v-if="!previewUrl">
                    <label class="btn file-upload">
                      画像を選択
                      <input type="file" @change="onFileSelected" accept="image/*" style="display: none" />
                    </label>
                  </template>
                  <!-- アップロード中の表示 -->
                  <template v-if="userStore.isUploadingIcon">
                    <button type="button" class="btn secondary" disabled>アップロード中...</button>
                  </template>
                  <template v-if="!previewUrl && userStore.formData.iconPath">
                    <button type="button" class="btn danger" @click="removeIcon">削除</button>
                  </template>
                </div>
              </div>
            </dd>
            <dt>メールアドレス</dt>
            <dd>
              <input type="email" name="user_email" v-model="userStore.formData.email" placeholder="メールアドレスを半角英数字で入力してください" required @input="handleEmailInput" :disabled="isEdit" :autocomplete="isEdit ? 'off' : 'email'" />
            </dd>

            <dt>名前</dt>
            <dd>
              <div class="name-grid">
                <input id="user-last-name" name="user_last_name" type="text" v-model="userStore.formData.lastName" v-autokana:user-last-name @input="handleFullWidthInput($event, 'lastName')" placeholder="姓" required autocomplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" data-bwignore="true" readonly onfocus="this.removeAttribute('readonly');" />
                <input id="user-first-name" name="user_first_name" type="text" v-model="userStore.formData.firstName" v-autokana:user-first-name @input="handleFullWidthInput($event, 'firstName')" placeholder="名" required autocomplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" data-bwignore="true" readonly onfocus="this.removeAttribute('readonly');" />
              </div>
            </dd>
            <dt>名前カナ</dt>
            <dd>
              <div class="name-grid">
                <input id="user-last-name-kana" name="user_last_kana" type="text" v-model="userStore.formData.lastKana" @input="handleFullWidthInput($event, 'lastKana')" placeholder="セイ" autocomplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" data-bwignore="true" readonly onfocus="this.removeAttribute('readonly');" />
                <input id="user-first-name-kana" name="user_first_kana" type="text" v-model="userStore.formData.firstKana" @input="handleFullWidthInput($event, 'firstKana')" placeholder="メイ" autocomplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" data-bwignore="true" readonly onfocus="this.removeAttribute('readonly');" />
              </div>
            </dd>
            <dt>短縮名</dt>
            <dd>
              <input type="text" name="user_short_name" v-model="userStore.formData.shortName" placeholder="短縮名を入力してください" autocomplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" data-bwignore="true" readonly onfocus="this.removeAttribute('readonly');" />
            </dd>
            <dt>権限</dt>
            <dd>
              <div class="select">
                <select name="role" v-model="userStore.formData.role" :disabled="userRoleDisabled">
                  <option v-for="option in userStore.getRoleOptions()" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>
            </dd>
            <dt>パスワード</dt>
            <dd v-if="isEdit">
              <button type="button" class="btn" @click="showPasswordDialog()">{{ isEdit ? 'パスワード変更' : 'パスワード設定' }}</button>
            </dd>
            <dd v-if="!isEdit">
              <input :type="showPassword ? 'text' : 'password'" name="new_user_password" id="password" v-model="userStore.formData.password" required autocomplete="new-password" />
              <button type="button" class="show-password" @click="showPassword = !showPassword">
                <span class="material-symbols-outlined">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </dd>
            <div class="border"></div>
            <dt>部門</dt>
            <dd>
              <input id="division" name="user_division" type="text" v-model="userStore.formData.division" v-autokana:division @input="handleFullWidthInput($event, 'division')" placeholder="全角で入力してください" autocomplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" data-bwignore="true" readonly onfocus="this.removeAttribute('readonly');" />
            </dd>
            <dt>部門カナ</dt>
            <dd>
              <input id="division-kana" name="user_division_kana" type="text" v-model="userStore.formData.divisionKana" @input="handleFullWidthInput($event, 'divisionKana')" placeholder="全角カタカナで入力してください" autocomplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" data-bwignore="true" readonly onfocus="this.removeAttribute('readonly');" />
            </dd>
            <dt>役職</dt>
            <dd>
              <input name="user_title" type="text" v-model="userStore.formData.title" @input="handleFullWidthInput($event, 'title')" placeholder="全角で入力してください" autocomplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" data-bwignore="true" readonly onfocus="this.removeAttribute('readonly');" />
            </dd>

            <div class="border"></div>
            <dt>通知メール</dt>
            <dd>
              <div class="checkboxs">
                <label>
                  <input name="notificationElectronicApplication" type="checkbox" v-model="userStore.formData.notifications.electronicApplication" />
                  電子申請
                </label>
                <label>
                  <input name="notificationSubsidy" type="checkbox" v-model="userStore.formData.notifications.subsidy" />
                  助成金
                </label>
                <label>
                  <input name="notificationAgreement" type="checkbox" v-model="userStore.formData.notifications.agreement" />
                  36協定
                </label>
              </div>
            </dd>
            <dt v-if="isEdit">OTP</dt>
            <dd v-if="isEdit">
              <div class="select small">
                <select name="isOtpEnabled" disabled :value="userStore.formData.isOtpEnabled">
                  <option value="false">無効</option>
                  <option value="true">有効</option>
                </select>
              </div>
            </dd>
            <dt v-if="isEdit && userStore.isShowingCurrentUser()"></dt>
            <dd v-if="isEdit && userStore.isShowingCurrentUser()">
              <button type="button" class="btn" @click="onOTPClick">{{ !userStore.formData.isOtpEnabled ? 'OTP有効化' : 'OTP無効化' }}</button>
            </dd>
            <dt v-if="isEdit">
              パスワード
              <br />
              サインイン
            </dt>
            <dd v-if="isEdit">
              <div class="select small">
                <select name="isPasswordSigninEnabled" v-model.boolean="userStore.formData.isPasswordSigninEnabled" :disabled="!userStore.isShowingCurrentUser()">
                  <option :value="false">無効</option>
                  <option :value="true">有効</option>
                </select>
              </div>
            </dd>
            <dt v-if="isEdit && userStore.isShowingCurrentUser()"></dt>
            <dd v-if="isEdit && userStore.isShowingCurrentUser()">
              <button type="button" class="btn" @click="openPasskeyDialog">パスキー登録</button>
            </dd>
            <dt v-if="isEdit">パスキー</dt>
            <dd v-if="isEdit">
              <!-- パスキー登録がなければ未登録、と表示 -->
              <div v-if="!userStore.formData.passkeys || userStore.formData.passkeys.length === 0" class="value-label">未登録</div>
              <table class="passkey" v-if="userStore.formData.passkeys && userStore.formData.passkeys.length > 0">
                <thead>
                  <tr>
                    <th>作成日</th>
                    <th>名前</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="passkey in userStore.formData.passkeys" :key="passkey.id">
                    <td>{{ formatDate(passkey.createdAt) }}</td>
                    <td>{{ passkey.name }}</td>
                    <td v-if="userStore.isShowingCurrentUser()">
                      <button type="button" class="btn small" @click="deletePasskey(passkey.id)">削除</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </dd>
          </dl>
        </form>
      </div>
    </template>
  </EditDialog>

  <!-- パスワード設定ダイアログ -->
  <EditDialog v-model:visible="showPopupPassword" small @save="changePassword" @delete="showPopupPassword = false" :show-copy-button="false" save-button-text="OK" delete-button-text="キャンセル" :disabled="isDisabledPasswordSave">
    <template v-slot:title>{{ isEdit ? 'パスワード変更' : 'パスワード設定' }}</template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <dt v-if="isEdit && userStore.isShowingCurrentUser()">今のパスワード</dt>
          <dd v-if="isEdit && userStore.isShowingCurrentUser()">
            <div class="password">
              <input name="currentPassword" v-if="showPasswordCurrent" type="text" v-model="currentPassword" />
              <input name="currentPassword" v-else type="password" v-model="currentPassword" />
              <button type="button" class="show-password" @click="showPasswordCurrent = !showPasswordCurrent">
                <span class="material-symbols-outlined">{{ showPasswordCurrent ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </dd>
          <dt>新しいパスワード</dt>
          <dd>
            <div class="password">
              <input name="newPassword" v-if="showPasswordChange" type="text" v-model="newPassword" />
              <input name="newPassword" v-else type="password" v-model="newPassword" />
              <button type="button" class="show-password" @click="showPasswordChange = !showPasswordChange">
                <span class="material-symbols-outlined">{{ showPasswordChange ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>

  <!-- パスキー登録ダイアログ -->
  <EditDialog v-model:visible="showPopupPasskey" small @save="registerPasskey" @delete="showPopupPasskey = false" :show-copy-button="false" save-button-text="OK" delete-button-text="キャンセル" :disabled-save-button="!currentPassword || !passkeyName">
    <template v-slot:title>パスキー登録</template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <dt>パスワード</dt>
          <dd>
            <div class="password">
              <input name="currentPasswordPasskey" v-if="showPasswordCurrent" type="text" v-model="currentPassword" />
              <input name="currentPasswordPasskey" v-else type="password" v-model="currentPassword" />
              <button type="button" class="show-password" @click="showPasswordCurrent = !showPasswordCurrent">
                <span class="material-symbols-outlined">{{ showPasswordCurrent ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </dd>
          <dt>パスキー名</dt>
          <dd>
            <input name="passkeyName" type="text" v-model="passkeyName" />
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>

  <!-- メールアドレス変更ダイアログ -->
  <EditDialog v-model:visible="showPopupEmailChange" @save="changeUserEmail" @delete="showPopupEmailChange = false" :show-copy-button="false" save-button-text="変更" delete-button-text="キャンセル" :disabled-save-button="!newEmail">
    <template v-slot:title>メールアドレス変更</template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <dt>新しいメールアドレス</dt>
          <dd>
            <input type="email" name="newEmail" v-model="newEmail" placeholder="新しいメールアドレスを入力してください" @input="handleNewEmailInput" />
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>
</template>

<style lang="less" scoped>
  .profile-image-preview {
    img {
      display: block;
      width: 200px;
      aspect-ratio: 1;
      object-fit: cover;
      border-radius: 50%;
    }
  }
  .profile-image-controls {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .value-label {
    margin-top: 5px !important;
  }
</style>
